'use client';
// 인앱 브라우저(카톡·라인·인스타·페북·텔레그램·네이버 등)에서 열리면 즉시 기본 브라우저로 자동 리다이렉트.
// 안드=기본 브라우저(intent, package 생략) / 카톡·라인=공식 스킴 / iOS=크롬 우선, 미설치 시 사파리 폴백.
// 오버레이/안내 없음 — 그냥 빠져나간다. 루프 가드는 '시간 기반'(sessionStorage에 {url, 시각} 기록).
//   같은 URL을 쿨다운(8초) 안에 다시 로드 = 탈출 실패로 인한 자동 재로드(루프)로 보고 스킵 → 무한 새로고침 차단.
//   8초 지난 뒤 수동 새로고침/재방문이거나 다른 URL이면 다시 시도(영구 스킵 아님).
import { useEffect } from 'react';

let fired = false;
const ESCAPE_COOLDOWN_MS = 8000; // 같은 URL을 이 시간 안에 다시 로드하면 '탈출 실패 자동 재로드(루프)'로 간주 → 스킵

export default function InAppBrowserEscape() {
  useEffect(() => {
    if (fired) return;
    // 루프 가드(시간 기반) — 같은 URL을 쿨다운 안에 다시 로드 = 탈출 실패로 인한 자동 재로드 → 스킵.
    // 충분히 지난 뒤의 수동 새로고침/재방문이나 '다른 URL'이면 다시 시도(영구 스킵 아님).
    const href = window.location.href;
    const now = Date.now();
    try {
      const prev = JSON.parse(sessionStorage.getItem('inapp_escape_last') || 'null');
      if (prev && prev.url === href && now - prev.ts < ESCAPE_COOLDOWN_MS) {
        fired = true;
        // 슬라이딩 — 빠른 연속 재로드(루프)가 계속되면 시각을 갱신해 계속 억제.
        sessionStorage.setItem('inapp_escape_last', JSON.stringify({ url: href, ts: now }));
        return;
      }
    } catch { /* sessionStorage 차단(시크릿/일부 인앱) → 모듈 fired가 같은 로드 내 1회 보장 */ }
    const ua = navigator.userAgent || '';
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isAndroid = /android/i.test(ua);
    const w = window as unknown as Record<string, unknown>;

    const isKakao = /KAKAOTALK/i.test(ua);
    const isLine = /Line\//i.test(ua);
    const isTelegram =
      'TelegramWebviewProxy' in w || 'TelegramWebview' in w || 'TelegramWebviewProxyProto' in w;
    const isInstagram = /Instagram/i.test(ua);
    const isFacebook = /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i.test(ua);
    const isNaver = /NAVER\(inapp/i.test(ua);
    const androidWv = isAndroid && /;\s*wv\)/.test(ua);
    const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
    const iosWebview = isIOS && !/Safari\//.test(ua) && !iosStandalone;

    const inApp =
      isKakao || isLine || isInstagram || isFacebook || isNaver || isTelegram || androidWv || iosWebview;
    if (!inApp) return;

    fired = true;
    // 시도 시각 기록 — 같은 URL을 쿨다운 안에 또 로드하면 위에서 스킵(루프 차단).
    try { sessionStorage.setItem('inapp_escape_last', JSON.stringify({ url: href, ts: now })); } catch { /* noop */ }
    const target = href;

    // 카카오톡 — 공식 스킴
    if (isKakao) {
      window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(target);
      return;
    }
    // 라인 — 공식 파라미터
    if (isLine) {
      if (/[?&]openExternalBrowser=1/.test(target)) return;
      const u = new URL(target);
      u.searchParams.set('openExternalBrowser', '1');
      window.location.href = u.toString();
      return;
    }
    // 안드로이드 — intent://로 기본(메인) 브라우저. package 생략 = 사용자 기본 브라우저, scheme=https 필수.
    if (isAndroid) {
      const u = new URL(target);
      window.location.href =
        `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;S.browser_fallback_url=${encodeURIComponent(target)};end`;
      return;
    }
    // iOS — 기본 브라우저 존중: 크롬 먼저(googlechromes://), 크롬 미설치 시에만 사파리로 폴백.
    // (iOS엔 '기본 브라우저로 열기' 단일 스킴이 없어, 크롬을 우선 시도하고 안 열리면 사파리.)
    if (isIOS) {
      const rest = target.replace(/^https?:\/\//, '');
      let left = false;
      const onHide = () => { if (document.hidden) left = true; };
      document.addEventListener('visibilitychange', onHide, { once: true });
      window.location.href = 'googlechromes://' + rest;          // 크롬으로
      setTimeout(() => {
        document.removeEventListener('visibilitychange', onHide);
        if (!left && !document.hidden) window.location.href = 'x-safari-https://' + rest; // 크롬 없으면 사파리
      }, 800);
      return;
    }
  }, []);

  return null;
}
